<template>
  <div
    class="
      tw-flex
      tw-items-center
      tw-gap-4
      tw-px-4
      tw-py-3
      tw-border-sf-snow-dark
      tw-relative
    "
    :class="borderClass"
    data-testid="pulse-user-row"
  >
    <div>
      <SfCheckbox
        :model-value="user.shared"
        :disabled="isDisabled"
        @update:model-value="handleSelect"
      ></SfCheckbox>
    </div>

    <div class="tw-min-h-[26px]">
      <span>{{ user.name }}</span>
      <span class="tw-text-sf-gray-light tw-pl-1">
        {{ user.email }}
      </span>
    </div>

    <SfDropdown
      v-if="user.shared"
      trigger-size="small"
      drawer-direction="left"
      :dropdown-id="dropdownId"
      :is-open="isScopedDropdownOpen"
      class="tw-ml-auto"
      @dropdown-clicked="handleToggleDropdown"
    >
      <template #trigger>
        <div class="tw-w-[104px]">
          {{ shareTypeName(user.shareType) }}
        </div>
      </template>
      <template #menu>
        <SfDropdownItem
          v-for="shareType in shareTypes"
          :key="shareType"
          :class="shareTypeClass(shareType)"
          class="tw-w-72"
          @click="updateShareType(shareType)"
        >
          <div>
            <div class="tw-font-bold">{{ shareTypeName(shareType) }}</div>
            <div class="sf-c-text-light">
              {{ shareTypeText(shareType) }}
            </div>
          </div>
        </SfDropdownItem>
      </template>
    </SfDropdown>
  </div>
</template>

<script setup lang="ts">
import type {
  PulseShareUser,
  UserLocalUpdatePayload,
  PulseShareType,
} from '../../shared/types/pulse-share'
import { computed } from 'vue'
import { storeToRefs } from 'pinia'

import { useDropdown } from '@app/shared/pinia/dropdown'
import SfCheckbox from '@app/shared/vue/components/sf-checkbox.vue'
import SfDropdown from '@app/shared/vue/components/sf-dropdown/sf-dropdown.vue'
import SfDropdownItem from '@app/shared/vue/components/sf-dropdown/sf-dropdown-item.vue'
import { pulseShareTypes } from '../../shared/types/pulse-share'

const I18n = window.I18n

interface Props {
  user: PulseShareUser
  isDisabled: boolean
  activeShareTypeDropdownUserId: number
  last: boolean
}

const props = defineProps<Props>()

const borderClass = computed(() => {
  return props.last ? '' : 'sf-layer-border-bottom'
})

const dropdownStore = useDropdown()

const { currentDropdownId } = storeToRefs(dropdownStore)
const dropdownId = 'pulse-share-type-dropdown'

const isScopedDropdownOpen = computed(() => {
  return (
    props.user.id === props.activeShareTypeDropdownUserId &&
    currentDropdownId.value === dropdownId
  )
})

const shareTypes = [pulseShareTypes.PREPARE, pulseShareTypes.REVIEW]

const emit =
  defineEmits<{
    updateUser: [payload: UserLocalUpdatePayload, userId: number]
    toggleShareTypeDropdown: [userId: number]
  }>()

function handleSelect(selected: boolean) {
  emit('updateUser', { shared: selected }, props.user.id)
}

function updateShareType(shareType: PulseShareType) {
  dropdownStore.closeDropdown()
  emit('updateUser', { shareType }, props.user.id)
}

function handleToggleDropdown() {
  emit('toggleShareTypeDropdown', props.user.id)
  dropdownStore.toggleDropdown(dropdownId)
}

function shareTypeClass(shareType: PulseShareType) {
  return shareType === props.user.shareType ? 'sf-bg-primary-subtle' : ''
}

function shareTypeText(shareType: PulseShareType) {
  return I18n.t(shareType.toString(), {
    scope: 'pulse.sharing.workflow_share_type_descriptions',
  })
}

function shareTypeName(shareType: PulseShareType | undefined) {
  return I18n.t((shareType || pulseShareTypes.PREPARE).toString(), {
    scope: 'pulse.sharing.workflow_share_types',
  })
}
</script>
